// React
import { useCallback } from 'react';
import { useLoaderData } from 'react-router-dom';

// Hooks
import { usePreviewData } from '@/hooks/usePreviewData';

// Utils
import { getSlug } from '@/utils/slug';

// Services
import { getHeader } from '@/services/header/headerService';
import { getFooter } from '@/services/footer/footerService';
import { getClient } from '@/services/client/clientService';

// Components
import { PageBlocks } from '@/components/blocks/Blocks';
import LayoutHeaderFooter from '@/components/layout/LayoutHeaderFooter/LayoutHeaderFooter';
import ClientHeroSection from '@/components/organisms/ClientHeroSection/ClientHeroSection';

// Styles
import { Helmet } from 'react-helmet';
import { RoutesEnum } from '@/enum/RoutesEnum';
import styles from './Client.module.css';

export async function clientLoader({ request }: { request: Request }) {
  const slug = getSlug(new URL(request.url));

  if (!slug) return null;

  const [header, footer, page] = await Promise.all([getHeader(), getFooter(), getClient(slug)]);

  return {
    header,
    footer,
    page,
  };
}

function Client() {
  const loaderData = useLoaderData() as Awaited<ReturnType<typeof clientLoader>>;
  const refetch = useCallback(
    () => clientLoader({ request: new Request(window.location.href) }),
    [],
  );
  const data = usePreviewData(loaderData, refetch);
  const baseUrl = import.meta.env.VITE_DOMAIN;

  if (!data) return null;

  const { header, footer, page } = data;

  if (!page) return null;

  return (
    <main>
      <Helmet>
        <title>{page.seo.metaTitle}</title>
        <meta name="description" content={page.seo.metaDescription} />
        <link rel="canonical" href={`${baseUrl}/${RoutesEnum.CLIENTS}/${page.slug}`} />
      </Helmet>
      <LayoutHeaderFooter header={header} footer={footer}>
        <div className={styles.body}>
          <ClientHeroSection
            client={page.client}
            title={page.title}
            paragraph={page.paragraph}
            tags={page.tags}
            gradient={page.gradient}
            shape={page.shape}
          />
          <PageBlocks blocks={page.blocks} />
        </div>
      </LayoutHeaderFooter>
    </main>
  );
}

export default Client;
